<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ t('general.contacts') }}
        </h6>

        <CreateButton
            :label="t('address_book.create_doctor')"
            :to="{ name: 'addressBookDoctorForm' }"/>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ t('address_book.doctor') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            @search="getDoctors"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getDoctors"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!doctors?.data?.length"
            icon="fa-address-book"
            title="Keine Kontakte gefunden"
            subtitle="Es wurden keine Kontakte gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click="sortBy('firstName')">
                  <span>{{ t('general.first_name') }}</span>
                  <i :class="getIconClass('firstName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('lastName')">
                  <span>{{ t('general.last_name') }}</span>
                  <i :class="getIconClass('lastName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('company')">
                  <span>{{ t('general.company_client') }}</span>
                  <i :class="getIconClass('company')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('phoneNumber')">
                  {{ t('general.phone_number') }}
                  <i :class="getIconClass('phoneNumber')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('email')">
                  {{ t('general.email') }}
                  <i :class="getIconClass('email')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('address')">
                  {{ t('general.street') }}
                  <i :class="getIconClass('address')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('zip')">
                  {{ t('general.postal_code') }}
                  <i :class="getIconClass('zip')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('city')">
                  {{ t('general.city') }}
                  <i :class="getIconClass('city')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('position')">
                  {{ t('general.position') }}
                  <i :class="getIconClass('position')"></i>
                </a>
              </th>
              <th>
                <a href="#">{{ t('general.info') }}</a>
              </th>
              <th style="min-width: 130px!important; text-align: center">
                <a href="#">{{ t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(doctor) in doctors?.data" :key="doctor.id">
              <td>{{ doctor?.firstName }}</td>
              <td>{{ doctor?.lastName }}</td>
              <td>{{ doctor?.company }}</td>
              <td>{{ doctor?.phoneNumber }}</td>
              <td>{{ doctor?.email }}</td>
              <td>{{ doctor?.address }}</td>
              <td>{{ doctor?.zip }}</td>
              <td>{{ doctor?.city }}</td>
              <td>{{ doctor?.position }}</td>
              <td>{{ doctor?.info ? 'JA' : 'NEIN' }}</td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({name: 'addressBookDoctorForm', params: {id: doctor?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2"
                        @click="deleteModal(doctor?.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination
            v-if="doctors?.pagination?.total_pages ?? 0 > 1"
            :totalPages="doctors?.pagination?.total_pages"
            :currentPage="doctors?.pagination?.current_page"
            :totalItems="doctors?.pagination?.total_items"
            :perPage="doctors?.pagination?.items_per_page"
            @pagechanged="getDoctors"/>
      </div>
    </div>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDeleteDoctor">
    </ModalDelete>

  </PageTemplate>
</template>

<script setup lang="ts">
import doctorService from '@/api/AddressBook/doctor';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import Search from '../Search.vue';
import {IAddressBookFilters, IAddressBookList} from "@/interfaces/AddressBook/IAddressBook";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();
const doctors = ref<IAddressBookList>();
const showModal = ref(false);
const doctorId = ref<number | null>(null);
const isSearching = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

const filters = ref<IAddressBookFilters>({
  firstName: '',
  lastName: '',
  city: '',
  company: '',
  phoneNumber: '',
  position: '',
  email: '',
  orderBy: 'DESC',
  sortBy: '',
  page: 1,
  limit: 10,
});

async function getDoctors(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IAddressBookFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = doctors.value?.pagination?.items_per_page;

  try {
    const {data} = await doctorService.getDoctors({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    doctors.value = data;
    isSearching.value = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

async function confirmDeleteDoctor() {
  try {
    await doctorService.deleteDoctor(doctorId.value!);
    await getDoctors();
    showModal.value = false;

    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

function deleteModal(id: number) {
  CloseToast();
  doctorId.value = id;
  showModal.value = true;
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getDoctors();
}

async function getExcelExport() {
  await downloadFile('/address-book/doctor/excel-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  CloseToast()
  await getDoctors();

  isLoaded.value = true;
});
</script>

<style scoped>

</style>